import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { Button, DatePicker, Form, Input, Modal, Select, Spin, Upload, notification } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from '@plugins/local-dayjs'

import {
  getExamDictData,
  getInterviewExamineesFields,
  updateImportInterviewExaminees,
  uploadInterviewPhoto
} from '../../common/request'
import ResponseCode from '../../common/response-code'
import { FieldTypeMap } from '../../common/common'
import { updateDictInfo } from '../../store/slice/dict-slice'
import { generateFileUrl } from '../../plugins/utils'
import {
  buildInterviewSaveData,
  parseSelectContentOptions,
  validateIdCard,
  validatePhotoFile
} from './interviewImportConstants'

const EditImportInterviewModal = forwardRef((props, ref) => {
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const dictInfo = useSelector(state => state.dict.dictInfo)
  const envInfo = useSelector(state => state.env.envInfo)
  const selectedExam = useSelector(state => state.examInfo.selectedExam)
  const [modalVisible, setModalVisible] = useState(false)
  const [fieldsLoading, setFieldsLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [photoUploading, setPhotoUploading] = useState(false)
  const [templateFields, setTemplateFields] = useState([])
  const [recordId, setRecordId] = useState('')
  const [photoPreview, setPhotoPreview] = useState('')

  useImperativeHandle(ref, () => ({
    showModal
  }))

  const loadDictIfNeeded = (field) => {
    if (field.fieldType !== FieldTypeMap.select || !field.fieldJoinSelect || dictInfo[field.fieldJoinSelect]) {
      return Promise.resolve()
    }

    return getExamDictData({ dictCode: field.fieldJoinSelect }).then(res => {
      if (res.code === ResponseCode.success) {
        dispatch(updateDictInfo({ dictKey: field.fieldJoinSelect, dictValue: res.data }))
      }
    })
  }

  const normalizeFieldValue = (field, rowValue) => {
    if (rowValue == null || rowValue === '') {
      return undefined
    }

    if (field.fieldType === FieldTypeMap.dateRangePicker) {
      const parsed = dayjs(rowValue)
      return parsed.isValid() ? parsed : undefined
    }

    return rowValue
  }

  const buildInitialValues = (row, fields) => {
    const values = {
      personalPhoto: row.personalPhoto
    }

    fields.forEach(field => {
      if (field.fieldCode && field.fieldCode !== 'personalPhoto') {
        values[field.fieldCode] = normalizeFieldValue(field, row[field.fieldCode])
      }
    })

    return values
  }

  const showModal = (row) => {
    setRecordId(row._id)
    setPhotoPreview(row.personalPhoto || '')
    setModalVisible(true)
    setFieldsLoading(true)
    form.resetFields()

    getInterviewExamineesFields({ id: row._id, examId: selectedExam.id }).then(res => {
      if (res.code !== ResponseCode.success) {
        setFieldsLoading(false)
        Modal.error({
          title: '错误',
          content: res.msg || '查询面试导入字段失败'
        })
        return
      }

      const fields = (Array.isArray(res.data) ? res.data : res.data?.fields || [])
        .slice()
        .sort((a, b) => (a.fieldSort || 0) - (b.fieldSort || 0))
        .filter(field => field.fieldCode !== 'personalPhoto')

      Promise.all(fields.map(field => loadDictIfNeeded(field))).finally(() => {
        setTemplateFields(fields)
        form.setFieldsValue(buildInitialValues(row, fields))
        setFieldsLoading(false)
      })
    }).catch(err => {
      console.error(err)
      setFieldsLoading(false)
      Modal.error({
        title: '错误',
        content: '网络错误，查询面试导入字段失败'
      })
    })
  }

  const hideModal = () => {
    setModalVisible(false)
    setTemplateFields([])
    setRecordId('')
    setPhotoPreview('')
    form.resetFields()
  }

  const getFieldRules = (field) => {
    const rules = []

    if (field.fieldIsNotBlank === '1') {
      rules.push({
        required: true,
        message: `${ field.fieldName }不能为空`
      })
    }

    if (field.fieldCode === 'idCard') {
      rules.push({
        validator: (_, value) => {
          if (!value || validateIdCard(value)) {
            return Promise.resolve()
          }
          return Promise.reject(new Error('身份证号码格式不正确'))
        }
      })
    }

    return rules
  }

  const generateFormComponent = (field) => {
    switch (field.fieldType) {
      case FieldTypeMap.textarea:
        return <Input.TextArea placeholder="请输入" rows={ 3 } style={{ width: 480 }} />
      case FieldTypeMap.select:
        if (field.fieldJoinSelect) {
          return (
            <Select
              placeholder="请选择"
              style={{ width: 480 }}
              options={ dictInfo[field.fieldJoinSelect]?.map(dict => ({
                label: dict.dictLabel,
                value: dict.dictValue
              })) }
            />
          )
        }
        return (
          <Select
            placeholder="请选择"
            style={{ width: 480 }}
            options={ parseSelectContentOptions(field.selectContent) }
          />
        )
      case FieldTypeMap.dateRangePicker:
        return (
          <DatePicker
            showTime
            style={{ width: 480 }}
            format="YYYY-MM-DD HH:mm"
          />
        )
      case FieldTypeMap.phone:
      case FieldTypeMap.mail:
      case FieldTypeMap.input:
      default:
        return <Input placeholder="请输入" style={{ width: 480 }} />
    }
  }

  const beforePhotoUpload = (file) => {
    if (!validatePhotoFile(file)) {
      Modal.error({
        title: '错误',
        content: '图片不合符要求'
      })
      return Upload.LIST_IGNORE
    }
    setPhotoUploading(true)
    return true
  }

  const handlePhotoChange = ({ file }) => {
    const res = file.response
    if (!res) {
      return
    }

    setPhotoUploading(false)
    if (res.code === ResponseCode.success) {
      const filePath = res.data?.filePath
      form.setFieldValue('personalPhoto', filePath)
      setPhotoPreview(filePath)
    } else {
      Modal.error({
        title: '错误',
        content: res.msg || '上传照片失败'
      })
    }
  }

  const handleSave = () => {
    form.validateFields().then(values => {
      const formattedValues = { ...values }
      templateFields.forEach(field => {
        if (field.fieldType === FieldTypeMap.dateRangePicker && formattedValues[field.fieldCode]) {
          formattedValues[field.fieldCode] = dayjs(formattedValues[field.fieldCode]).format('YYYY-MM-DD HH:mm')
        }
      })

      const payload = {
        id: recordId,
        examId: selectedExam.id,
        data: buildInterviewSaveData({
          ...formattedValues,
          personalPhoto: formattedValues.personalPhoto ?? photoPreview
        }, templateFields)
      }

      setSaveLoading(true)
      updateImportInterviewExaminees(payload).then(res => {
        setSaveLoading(false)
        if (res.code === ResponseCode.success) {
          notification.success({
            message: '数据修改成功'
          })
          hideModal()
          props.onSuccess?.()
        } else {
          Modal.error({
            title: '错误',
            content: res.msg || '保存失败'
          })
          if (res.msg?.includes('身份证号码重复')) {
            form.scrollToField('idCard')
          }
        }
      }).catch(err => {
        console.error(err)
        setSaveLoading(false)
        Modal.error({
          title: '错误',
          content: '网络错误，保存失败'
        })
      })
    }).catch(() => {})
  }

  return (
    <Modal
      title="编辑面试导入考生"
      width={ 720 }
      open={ modalVisible }
      destroyOnClose
      onCancel={ hideModal }
      footer={ [
        <Button key="cancel" onClick={ hideModal }>取消</Button>,
        <Button
          key="save"
          type="primary"
          loading={ saveLoading }
          onClick={ handleSave }
        >保存</Button>
      ] }
    >
      <Spin spinning={ fieldsLoading }>
        <div className="mb-4 d-flex align-items-center">
          {
            photoPreview
              ? (
                <img
                  alt="个人照片"
                  src={ generateFileUrl(envInfo, photoPreview) }
                  style={{ width: 96, height: 128, objectFit: 'cover', borderRadius: 4 }}
                />
              )
              : (
                <div
                  style={{
                    width: 96,
                    height: 128,
                    background: '#f5f5f5',
                    borderRadius: 4,
                    lineHeight: '128px',
                    textAlign: 'center',
                    color: '#999'
                  }}
                >
                  暂无照片
                </div>
              )
          }
          <Upload
            className="ml-3"
            name="file"
            action={ uploadInterviewPhoto }
            headers={{
              authorization: window.sessionStorage.getItem('access_token')
            }}
            data={{ examId: selectedExam.id }}
            showUploadList={ false }
            beforeUpload={ beforePhotoUpload }
            onChange={ handlePhotoChange }
          >
            <Button loading={ photoUploading }>更换照片</Button>
          </Upload>
        </div>

        <Form
          form={ form }
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 16 }}
        >
          <Form.Item name="personalPhoto" hidden>
            <Input />
          </Form.Item>
          {
            templateFields.map(field => (
              <Form.Item
                key={ field.fieldCode }
                label={ field.fieldName }
                name={ field.fieldCode }
                rules={ getFieldRules(field) }
              >
                { generateFormComponent(field) }
              </Form.Item>
            ))
          }
        </Form>
      </Spin>
    </Modal>
  )
})

export default EditImportInterviewModal
